import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  computed,
  input,
  signal,
} from '@angular/core';
import {
  AGMedia,
  AGMediaKeys,
  Constants,
  EmailTemplateContextMap,
  LookupKeys,
  MediaSize,
  Messages,
  Patterns,
} from '@ag-common-lib/public-api';
import { validateDxGroups, validateUrlWithOrWithoutProtocol } from 'ag-common-svc/lib/utils/validation';
import {
  DxDateBoxComponent,
  DxHtmlEditorComponent,
  DxPopoverComponent,
  DxSelectBoxComponent,
  DxTagBoxComponent,
  DxTextBoxComponent,
  DxValidatorComponent,
} from 'devextreme-angular';
import { InitializedEvent as HtmlEditorInitializedEvent } from 'devextreme/ui/html_editor';
import { HtmlEditorToolbarItemsPipe } from './html-editor-toolbar-items.pipe';
import { ClickEvent } from 'devextreme/ui/button';
import { ShowingEvent } from 'devextreme/ui/popover';
import { ItemClickEvent, KeyDownEvent } from 'devextreme/ui/select_box';
import { customizeQuill } from './html-editor.utils';
import { buildPastedDelta, clipboardHasHtml, isMatchStyleShortcut, pasteTextColor } from './html-editor-paste.utils';
import {
  BUTTON_ACTION_TYPE_LOOKUP,
  BUTTON_NAVIGATE_TO_LOOKUP,
  BUTTON_TYPE_LOOKUP,
  ButtonActionType,
  ButtonNavigateTo,
  HREF_TARGET_LOOKUP,
} from '../administration/template-builder/template-editor-page/models/template-builder-button-widget.model';
import { HtmlEditorLinkService } from './html-editor-link-form.service';
import Quill from 'devextreme-quill';
import { HtmlEditorType } from './html-editor.model';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { TemplateLineHeightLookup } from '../administration/template-builder/template-editor-page/models';
import { PortalPagesDataService } from 'ag-common-svc/lib/utils/portal-pages-data-service';
import { AgMediaUploaderModalComponent } from 'ag-common-svc/lib/components/ag-media-uploader/ag-media-uploader-modal.component';
import {
  DEFAULT_MAX_MEDIA_UPLOAD_SIZE_MB,
  MediaUploaderTabs,
} from 'ag-common-svc/lib/components/ag-media-uploader/ag-media-uploader-modal.models';
import { WasabiImgPipe } from 'ag-common-svc/shared/pipes/wasabi-img.pipe';
import {
  LINK_STYLE_FROM_GLOBAL,
  LINK_UNDERLINE_SELECT_OPTIONS,
} from './html-editor-link-styling.utils';

const IMAGE_READY_RETRY_DELAY_MS = 500;
const IMAGE_READY_TIMEOUT_MS = 60_000;

export type FormEditors =
  | DxHtmlEditorComponent
  | DxTextBoxComponent
  | DxSelectBoxComponent
  | DxDateBoxComponent
  | DxTagBoxComponent;

export interface SaveActionPayload {
  component: FormEditors;
  validator?: DxValidatorComponent;
}

export enum HtmlEditorVariableType {
  url = 'url',
  email = 'email',
  phone = 'phone',
}

export interface HtmlEditorVariableItem {
  value?: string;
  description?: string;
  type?: HtmlEditorVariableType;
}

export interface HtmlEditorVariable {
  key?: string;
  collectionDescription?: string;
  items: HtmlEditorVariableItem[];
}

@Component({
  selector: 'shr-html-editor',
  templateUrl: './html-editor.component.html',
  styleUrls: ['./html-editor.component.scss'],
  providers: [HtmlEditorToolbarItemsPipe, HtmlEditorLinkService, PortalPagesDataService, WasabiImgPipe],
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HtmlEditorComponent implements OnDestroy {
  @ViewChild('editorRef') editorComponent: DxHtmlEditorComponent;
  @ViewChild('editorContainerRef') editorContainerRef;
  @ViewChild('contentContainerRef') contentContainerRef;
  @ViewChild(AgMediaUploaderModalComponent, { static: false })
  mediaUploaderComponent: AgMediaUploaderModalComponent;
  @HostBinding('class') className = 'html-editor';
  @Input() value?: string;
  @Input() label?: string;
  @Input() validationGroup?: string;
  @Input() name: string;
  @Output() valueChange: EventEmitter<string> = new EventEmitter();
  @Input() isRequired = false;
  readonly isReadOnlySig = input(false, { alias: 'isReadOnly' });
  @Input() useFiledEditing = false;
  @Input() hasChanges = false;
  @Input() inProgress = false;
  @Input() fileDirectory: string = 'html-editor';
  @Input() mediaSources: MediaUploaderTabs[] = [MediaUploaderTabs.FromFile, MediaUploaderTabs.ByURL];
  @Input() mediaItems: AGMedia[];
  @Input() maxFileSizeMb = DEFAULT_MAX_MEDIA_UPLOAD_SIZE_MB;
  @Input() applyImageDownscale = false;
  @Input() allowSoftLineBreak?: boolean = false;
  @Input() presetColors = [];
  readonly variablesSig = input<HtmlEditorVariable[]>([], { alias: 'variables' });
  @Input() defaultFormats;
  @Input() htmlEditorType: HtmlEditorType = HtmlEditorType.paragraph;
  @Input() useWebNavigationOnly = false;

  readonly toolbarItemsSig = input<any>(null, { alias: 'toolbarItems' });
  protected toolbarItemsResolvedSig;

  @Output() handleFocusOut = new EventEmitter<void>();
  @Output() shortFilePath = new EventEmitter<string>();
  @Output() onShowPreview = new EventEmitter<string>();
  @Output() onHTMLEditorInitialized = new EventEmitter<HtmlEditorInitializedEvent>();

  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onSaveChanges: EventEmitter<SaveActionPayload> = new EventEmitter();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onRevertChanges: EventEmitter<DxHtmlEditorComponent> = new EventEmitter();

  protected readonly selectionFormattingSig = signal<any>({ background: 'transparent', color: 'transparent' });
  protected readonly Messages = Messages;

  protected BUTTON_TYPE_LOOKUP = BUTTON_TYPE_LOOKUP;
  protected BUTTON_ACTION_TYPE_LOOKUP = BUTTON_ACTION_TYPE_LOOKUP;
  protected BUTTON_NAVIGATE_TO_LOOKUP = BUTTON_NAVIGATE_TO_LOOKUP;
  protected ButtonActionType = ButtonActionType;
  protected LookupKeys = LookupKeys;
  protected HtmlEditorVariableType = HtmlEditorVariableType;
  protected HREF_TARGET_LOOKUP = HREF_TARGET_LOOKUP;
  protected readonly phoneMask: string = Constants.PHONE_MASK;
  protected phonePattern = Patterns.PHONE_NUMBER;
  protected readonly ButtonNavigateTo = ButtonNavigateTo;
  protected readonly BaseModelKeys = BaseModelKeys;
  protected readonly TemplateLineHeightLookup = TemplateLineHeightLookup;
  protected readonly LINK_STYLE_FROM_GLOBAL = LINK_STYLE_FROM_GLOBAL;
  protected readonly linkUnderlineOptions = LINK_UNDERLINE_SELECT_OPTIONS;
  protected readonly mouseoverStylingEmailHint =
    'Most email clients (especially Gmail mobile, Outlook mobile, and many webmail interfaces) strip or ignore hover for security';

  protected readonly linkStylePresetColorsSig = computed(() => [
    { value: LINK_STYLE_FROM_GLOBAL, description: 'From Global styles' },
    ...(this.presetColors ?? []),
  ]);

  private readonly initialValues = {};
  private selectedRange;
  protected selectedColor;

  protected quillRange;
  protected quillActiveElement: Element;
  protected managedLinkElement: Element | null = null;
  protected readonly isLinkPopoverOpenSig = signal(false);

  protected linkFormDataSig = this.htmlEditorLinkService.formDataSig;
  protected readonly linkFormValidationGroup = this.htmlEditorLinkService.linkFormValidationGroup;

  protected readonly portalPagesSig = this.portalPagesDataService.groupedPortalPagesSig;

  protected quill;
  protected readonly mentionItemsSig = computed(() => this.buildMentionItems(this.variablesSig()));
  protected readonly mentionsConfigSig = computed(() => [
    {
      marker: '{{',
      dataSource: this.mentionItemsSig(),
      displayExpr: 'description',
      valueExpr: 'mentionId',
      searchExpr: ['searchLabel', 'description', 'value', 'collectionDescription'],
      minSearchLength: 0,
      searchTimeout: 0,
      template: this.renderMentionTemplate,
      itemTemplate: this.renderMentionItemTemplate,
    },
  ]);

  private imageSelectionRange: { index: number; length: number } | null = null;
  private isUpdatingImage = false;
  private pasteKeepSourceColor = true;
  private pasteDestinationColor: string | undefined;
  private pasteMatchStyleRequested = false;
  private pasteMatchStyleTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(
    private htmlEditorLinkService: HtmlEditorLinkService,
    private htmlEditorToolbarItemsPipe: HtmlEditorToolbarItemsPipe,
    private portalPagesDataService: PortalPagesDataService,
    private wasabiImgPipe: WasabiImgPipe,
  ) {
    this.toolbarItemsResolvedSig = computed(() => {
      const data = this.toolbarItemsSig();
      const items = data ?? this.htmlEditorToolbarItemsPipe.transform();
      return this.withImageUploadTemplate(items);
    });
  }

  protected isFocusedSig = signal(false);
  protected readonly isImageUploadingSig = signal(false);

  protected get maxFileSizeLabel(): string {
    if (this.maxFileSizeMb < 1) {
      return `${Math.round(this.maxFileSizeMb * 1024)}KB`;
    }

    return `${this.maxFileSizeMb}MB`;
  }

  protected handleValueChange = e => {
    this.valueChange.emit(e?.value);
  };

  protected handleRevertClick = () => {
    this.onRevertChanges.emit(this.editorComponent);
  };

  protected handleSaveChangesClick = async () => {
    const isValid = await validateDxGroups(this.validationGroup);
    if (isValid) {
      this.onSaveChanges.emit({ component: this.editorComponent });
    }
  };

  protected onImageToolbarClick = (): void => {
    const quill = this.editorComponent?.instance?.getQuillInstance();
    if (!quill) {
      return;
    }

    const range = quill.getSelection(true) ?? this.quillRange ?? { index: quill.getLength(), length: 0 };
    this.imageSelectionRange = { index: range.index, length: range.length ?? 0 };
    this.isUpdatingImage = Object.prototype.hasOwnProperty.call(quill.getFormat(range) ?? {}, 'imageSrc');
    this.mediaUploaderComponent?.showModal();
  };

  protected onMediaChanged = async (media: AGMedia): Promise<void> => {
    const wasabiPath = media?.[AGMediaKeys.wasabiPath];
    if (!wasabiPath) {
      return;
    }

    const src = this.wasabiImgPipe.transform(wasabiPath, { mediaSize: MediaSize.origin });
    if (!src) {
      return;
    }

    this.isImageUploadingSig.set(true);

    try {
      await this.waitForImageReady(src);
      this.insertOrUpdateImage(src);
      this.shortFilePath.emit(wasabiPath);
    } finally {
      this.isImageUploadingSig.set(false);
    }
  };

  protected onEditorInitialized(e: HtmlEditorInitializedEvent) {
    // e.component.registerKeyHandler('enter', e => {
    //   e?.stopPropagation();
    //   e?.preventDefault();
    //   debugger;
    // });
    setTimeout(() => {
      const quill = e.component.getQuillInstance();

      this.quill = quill;
      this.registerPasteFormatting(quill);

      quill.on('selection-change', (range, oldRange, source) => {
        this.quillRange = range;

        if (!range) {
          return;
        }
        const selectedFormats = quill.getFormat(range);
        const format = Object.assign(
          { background: 'inherit', color: 'inherit', lineHeight: 'inherit' },
          // this.defaultFormats,
          selectedFormats,
        );

        if (this.quillActiveElement) {
          this.quillActiveElement?.classList?.remove('html-editor__active-element');
          this.quillActiveElement = null;
        }

        const activeLinkElement =
          this.resolveLinkElementAtRange(quill, range) ?? (format.id ? document.getElementById(format.id) : null);
        if (activeLinkElement) {
          this.quillActiveElement = activeLinkElement;
          this.quillActiveElement?.classList?.add('html-editor__active-element');
        }

        this.selectionFormattingSig.set(format);
      });

      quill.getSelection(true);
      this.registerMentionTrigger(quill);
    }, 0);

    this.onHTMLEditorInitialized.emit(e);
  }

  // Strips pasted formatting so outside fonts and sizes do not land in the block.
  // quill.clipboard.convert() also runs when DevExtreme loads the editor value
  // (_updateHtmlContent / _applyTranscludedContent), which previously wiped saved
  // bold/list/etc. formatting on every edit. Only strip during an actual user paste.
  // Text color is kept: a regular paste keeps the copied color, and Paste and Match
  // Style takes the color at the caret instead of the paragraph theme color.
  private registerPasteFormatting(quill): void {
    const Delta = Quill.import('delta');
    let isUserPasting = false;

    quill.root.addEventListener(
      'keydown',
      (event: KeyboardEvent) => {
        if (!isMatchStyleShortcut(event)) {
          return;
        }

        this.pasteMatchStyleRequested = true;
        this.clearPasteMatchStyleRequestTimer();
        this.pasteMatchStyleTimer = setTimeout(() => {
          this.pasteMatchStyleRequested = false;
          this.pasteMatchStyleTimer = undefined;
        }, 1000);
      },
      true,
    );

    quill.root.addEventListener(
      'paste',
      (event: ClipboardEvent) => {
        isUserPasting = true;
        const html = event.clipboardData?.getData('text/html');
        const types = event.clipboardData ? Array.from(event.clipboardData.types) : [];
        const matchStyle = this.pasteMatchStyleRequested || !clipboardHasHtml(types, html);
        this.pasteKeepSourceColor = !matchStyle;
        this.pasteDestinationColor = matchStyle ? this.readCaretTextColor(quill) : undefined;
        this.pasteMatchStyleRequested = false;
        this.clearPasteMatchStyleRequestTimer();
        setTimeout(() => {
          isUserPasting = false;
          this.pasteKeepSourceColor = true;
          this.pasteDestinationColor = undefined;
        }, 0);
      },
      true,
    );

    quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
      if (!isUserPasting) {
        return delta;
      }

      if (this.pasteKeepSourceColor && node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'A') {
        return delta;
      }

      return buildPastedDelta(Delta, delta?.ops, {
        keepSourceColor: this.pasteKeepSourceColor,
        destinationColor: this.pasteDestinationColor,
      });
    });

    quill.clipboard.addMatcher(Node.TEXT_NODE, (_node, delta) => {
      if (!isUserPasting || this.pasteKeepSourceColor) {
        return delta;
      }

      return buildPastedDelta(Delta, delta?.ops, {
        keepSourceColor: false,
        destinationColor: this.pasteDestinationColor,
      });
    });
  }

  private readCaretTextColor(quill): string | undefined {
    const range = quill.getSelection() ?? quill.getSelection(true);
    if (!range) {
      return undefined;
    }

    const formats = quill.getFormat(range.index, 0);
    return pasteTextColor(formats?.color);
  }

  private clearPasteMatchStyleRequestTimer(): void {
    if (!this.pasteMatchStyleTimer) {
      return;
    }

    clearTimeout(this.pasteMatchStyleTimer);
    this.pasteMatchStyleTimer = undefined;
  }

  private insertOrUpdateImage = (src: string): void => {
    const quill = this.editorComponent?.instance?.getQuillInstance();
    if (!quill) {
      return;
    }

    let index = this.imageSelectionRange?.index ?? quill.getLength();

    if (this.isUpdatingImage) {
      const formatIndex =
        this.imageSelectionRange?.length > 0 ? this.imageSelectionRange.index : Math.max(0, index - 1);
      quill.deleteText(formatIndex, 1, 'silent');
      index = formatIndex;
    }

    quill.insertEmbed(index, 'extendedImage', { src }, 'user');
    quill.setSelection(index + 1, 0, 'user');
    this.syncValueFromEditor();
  };

  private waitForImageReady = async (src: string): Promise<void> => {
    const startedAt = Date.now();

    while (Date.now() - startedAt < IMAGE_READY_TIMEOUT_MS) {
      const isReady = await this.tryLoadImage(src);
      if (isReady) {
        return;
      }

      await this.delay(IMAGE_READY_RETRY_DELAY_MS);
    }
  };

  private tryLoadImage = (src: string): Promise<boolean> => {
    return new Promise(resolve => {
      const image = new Image();
      image.onload = () => resolve(true);
      image.onerror = () => resolve(false);
      image.src = src;
    });
  };

  private delay = (ms: number): Promise<void> => {
    return new Promise(resolve => {
      setTimeout(resolve, ms);
    });
  };

  private withImageUploadTemplate = (items: any[]) => {
    if (!items?.length) {
      return items;
    }

    return items.map(item => {
      if (item?.name !== 'image' || item?.template) {
        return item;
      }

      return {
        ...item,
        template: 'imageUploadTmp',
        options: {
          ...(item.options ?? {}),
          hint: item.options?.hint ?? 'Upload Media',
        },
      };
    });
  };

  protected applyColor = (hex: string, propertyName) => {
    if (hex === this.selectedColor) {
      return;
    }

    this.selectedColor = hex;

    const quill = this.editorComponent.instance.getQuillInstance();

    const totalLength = quill.getLength(); // includes trailing newline

    const isSelectAll =
      this.selectedRange && this.selectedRange.index === 0 && this.selectedRange.length === totalLength - 1;

    quill.setSelection(this.selectedRange?.index ?? 0, this.selectedRange?.length ?? 0, 'silent');

    this.editorComponent.instance.format(propertyName, this.selectedColor);
  };

  protected handleHeaderChange = (e: ItemClickEvent) => {
    const quill = this.editorComponent.instance.getQuillInstance();
    const length = this.editorComponent.instance.getLength();
    const textContent = quill.getText(0, length);
    const format = quill.getFormat(0, length);

    quill.setContents([
      {
        insert: textContent,
        attributes: Object.assign({}, format, { header: `H${e.itemData}` }),
      },
    ]);
  };

  onSelectionChanged = e => {
    const selected = e?.item;
    const value = selected?.value;
    const quill = this.editorComponent.instance.getQuillInstance();
    quill.format('lineHeight', value, 'api');

    setTimeout(() => {
      this.selectionFormattingSig().lineHeight = value;
    }, 0);
  };

  protected clearFormat = () => {
    const quill = this.editorComponent.instance.getQuillInstance();
    const range = quill.getSelection(false);

    // quill.removeFormat(range?.index, range?.length, 'silent');

    quill.formatText(range?.index, range?.length, {
      color: false,
      size: false,
      font: false,
      background: false,
      lineHeight: false,
      italic: false,
      bold: false,
      strike: false,
      underline: false,
      align: false,
    });
  };

  protected customizeQuillModules = config => {
    // config.keyboard = {
    //   bindings: {
    //     custom: {
    //       key: ['enter', 'Enter', 13],
    //       shiftKey: false,
    //       handler: (range, context) => {
    //         const quill = this._quill;

    //         try {
    //           // debugger;
    //           quill.insertEmbed(range.index, 'softbreak', true, 'user');
    //           quill.setSelection(range.index + 1, 'silent');
    //         } catch (error) {
    //           debugger;
    //         }

    //         return true;
    //       },
    //     },
    //   },
    // };

    customizeQuill(config, this.htmlEditorType, this.selectionFormattingSig);

    return config;
  };

  protected onFocusIn = () => {
    this.isFocusedSig.set(true);
  };

  protected onFocusOut = () => {
    if (!this.isLinkPopoverOpenSig()) {
      this.syncValueFromEditor();
    }

    this.isFocusedSig.set(false);
    this.handleFocusOut.emit();
  };

  ngOnDestroy(): void {
    this.clearPasteMatchStyleRequestTimer();
    this.syncValueFromEditor();
  }

  // Reads quill.root.innerHTML and emits valueChange when it differs from the bound value.
  // The preview outside edit mode renders textWidgetData.innerHtml, which stayed stale.
  syncValueFromEditor(): void {
    if (this.isLinkPopoverOpenSig()) {
      return;
    }

    const quill = this.editorComponent?.instance?.getQuillInstance();

    if (!quill) {
      return;
    }

    const html =
      typeof quill.getSemanticHTML === 'function'
        ? quill.getSemanticHTML(0, quill.getLength())
        : quill.root.innerHTML;

    if (html === this.value) {
      return;
    }

    this.valueChange.emit(html);
  }

  protected cancelColorChanges = propertyName => {
    const quill = this.editorComponent.instance.getQuillInstance();
    const range = this.selectedRange;
    const index = range?.index ?? 0;
    const length = range?.length ?? 0;
    quill.setSelection(index, length, 'silent');

    const initialValue = this.initialValues[propertyName];
    this.selectionFormattingSig.update(data => {
      data[propertyName] = initialValue;
      return data;
    });

    quill.formatText(index, length, propertyName, initialValue, 'silent');
  };

  protected applyColorChanges = propertyName => {
    const quill = this.editorComponent.instance.getQuillInstance();
    const index = this.selectedRange?.index ?? 0;
    const length = this.selectedRange?.length ?? 0;

    quill.setSelection(index ?? 0, length ?? 0, 'silent');
    quill.format(propertyName, this.selectedColor);

    this.editorComponent.instance.getSelection(true);

    this.selectionFormattingSig()[propertyName] = this.selectedColor;
  };

  protected onToolbarItemShowing = (e: ShowingEvent, propertyName, value) => {
    this.selectedRange = this.editorComponent.instance.getSelection(false);
    this.initialValues[propertyName] = value;
    this.selectedColor = value;
  };

  protected handleOutsidePopoverClick = (e: Event) => {
    e?.stopPropagation();
    e?.preventDefault();
    return false;
  };

  protected manageLink = (e, linkPopover: DxPopoverComponent) => {
    e?.event?.stopPropagation();
    const quill = this.editorComponent.instance.getQuillInstance();
    const range = quill.getSelection(false) ?? this.quillRange;
    this.managedLinkElement = this.resolveLinkElementAtRange(quill, range) ?? this.quillActiveElement ?? null;
    this.htmlEditorLinkService.manageLink(quill, this.managedLinkElement);
    this.normalizeLinkNavigateMode();
    this.isLinkPopoverOpenSig.set(true);
    linkPopover.instance.show();
  };

  // Resolves the enclosing <a> from the cursor's DOM leaf so the correct link is
  // edited even when several links share the same id.
  private resolveLinkElementAtRange = (quill, range): Element | null => {
    if (!range) {
      return null;
    }

    const indices =
      range.length > 0
        ? [range.index, range.index + range.length - 1]
        : [range.index, range.index - 1];

    for (const index of indices) {
      const linkElement = this.findAnchorFromLeaf(quill, index);

      if (linkElement) {
        return linkElement;
      }
    }

    return null;
  };

  private findAnchorFromLeaf = (quill, index: number): Element | null => {
    if (index < 0) {
      return null;
    }

    const [leaf] = quill.getLeaf(index) ?? [];
    let node: Node | null = leaf?.domNode ?? null;

    while (node && node !== quill.root) {
      if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'A') {
        return node as Element;
      }

      node = node.parentNode;
    }

    return null;
  };

  protected cancelLinkChanges = (e: ClickEvent, linkPopover: DxPopoverComponent) => {
    e.event.stopPropagation();
    this.htmlEditorLinkService.revertAllChanges();
    this.hideLinkPopover(linkPopover);
  };

  protected onLinkPopoverHidden = () => {
    this.isLinkPopoverOpenSig.set(false);
    this.managedLinkElement = null;
  };

  protected onSelectBoxItemClick = (e: ItemClickEvent) => {
    e.event.stopPropagation();
  };

  protected onLinkTypeValueChanged = () => {
    this.normalizeLinkNavigateMode();
  };

  validateUrlWithoutProtocol = e => {
    return validateUrlWithOrWithoutProtocol(e?.value, Patterns.URL_PATTERN_WITH_OR_WITHOUT_ONLY_ONE_PROTOCOL);
  };

  protected addLink = async (linkPopover: DxPopoverComponent) => {
    this.normalizeLinkNavigateMode();
    const quill = this.editorComponent.instance.getQuillInstance();
    const isValid = await validateDxGroups(this.linkFormValidationGroup);

    if (!isValid) {
      return;
    }
    try {
      const activeElement = this.managedLinkElement ?? this.quillActiveElement;
      const linkElement = this.htmlEditorLinkService.addLink(quill, activeElement);

      if (linkElement) {
        if (this.quillActiveElement && this.quillActiveElement !== linkElement) {
          this.quillActiveElement.classList.remove('html-editor__active-element');
        }

        this.quillActiveElement = linkElement;
        this.quillActiveElement.classList.add('html-editor__active-element');
      }

      this.syncValueFromEditor();
      this.htmlEditorLinkService.formChangesDetector.clear();
      this.hideLinkPopover(linkPopover);
    } catch (error) {}
  };

  private hideLinkPopover = (linkPopover: DxPopoverComponent) => {
    this.isLinkPopoverOpenSig.set(false);
    this.managedLinkElement = null;
    linkPopover.instance.hide();
  };

  private normalizeLinkNavigateMode = () => {
    if (!this.useWebNavigationOnly) {
      return;
    }

    const formData = this.linkFormDataSig();
    if (!formData || formData?.type !== ButtonActionType.link) {
      return;
    }

    formData.navigateTo = ButtonNavigateTo.web;
    formData.portalPage = '';
  };

  protected handleVariableButtonClick = e => {
    e?.event?.stopPropagation();
    const quill = this.editorComponent?.instance?.getQuillInstance();
    if (!quill) {
      return;
    }

    quill.focus();
    const range = quill.getSelection(true);
    const index = range?.index ?? quill.getLength();
    quill.insertText(index, '{{', 'user');
    quill.setSelection(index + 2, 0, 'silent');
  };

  protected onSelectBoxWithVarInput = (e: KeyDownEvent) => {
    const selectedItem = e.component.option('selectedItem');

    if (!selectedItem || selectedItem?.isCustom) {
      return;
    }

    const key = e.event.key;
    const isAlphaNumeric = /^[\p{L}\p{N}]$/u.test(key);
    const value = isAlphaNumeric ? key : '';

    e.component.option('value', value);
    e.event.preventDefault();
    e.event.stopPropagation();
  };

  protected handleCustomItemCreating = e => {
    const description = e?.text;
    const value = this.linkFormDataSig().value ?? '';
    const customItem = { value, description, isCustom: true };

    Object.assign(e, { customItem });
  };

  protected lineHeightDisplayExp = item => {
    console.log('lineHeightDisplayExp item', item);

    return 'Def.';
  };

  private registerMentionTrigger(quill) {
    const Delta = Quill.import('delta');

    quill.keyboard.addBinding({ key: '{', shiftKey: true }, (range, context) => {
      if (!range || range.length) {
        return true;
      }

      const prevIndex = range.index - 1;
      if (prevIndex < 0) {
        return true;
      }

      const prevChar = quill.getText(prevIndex, 1);
      if (prevChar !== '{') {
        return true;
      }

      const delta = new Delta().retain(prevIndex).delete(1).insert('{{');
      quill.updateContents(delta, 'user');
      quill.setSelection(prevIndex + 2, 0, 'silent');
      return false;
    });
  }

  private buildMentionItems(variablesInput: HtmlEditorVariable[]) {
    const items = [];
    const variables = variablesInput ?? [];

    for (const group of variables) {
      if (!group?.items?.length) {
        continue;
      }

      const collection = group?.key ? String(group.key) : '';
      const collectionDescription = group?.collectionDescription ?? collection;
      const groupItems = group.items
        .filter(Boolean)
        .map(item => {
          const value = item?.value ? String(item.value) : '';
          const rawDescription = item?.description ?? value;
          if (!rawDescription && !value) {
            return null;
          }

          const displayLabel = this.stripVariableLabel(rawDescription ?? value);
          const mentionId = this.encodeMentionPayload({
            value,
            collection,
            description: rawDescription ?? displayLabel,
          });

          return {
            value,
            description: displayLabel,
            collection,
            collectionDescription,
            searchLabel: displayLabel,
            mentionId,
          };
        })
        .filter(Boolean)
        .sort((a, b) =>
          String(a.searchLabel ?? '').localeCompare(String(b.searchLabel ?? ''), undefined, {
            sensitivity: 'base',
            numeric: true,
          }),
        );

      for (let index = 0; index < groupItems.length; index += 1) {
        const item = groupItems[index];
        items.push({
          ...item,
          isFirstInGroup: index === 0,
        });
      }
    }

    return items;
  }

  private encodeMentionPayload(payload: { value: string; collection: string; description: string }): string {
    try {
      return encodeURIComponent(JSON.stringify(payload));
    } catch (error) {
      return '';
    }
  }

  private stripVariableLabel(label: string): string {
    return String(label)
      .replace(/^\s*\{\{\s*/, '')
      .replace(/\s*\}\}\s*$/, '')
      .trim();
  }

  private renderMentionTemplate(data, element) {
    const value = data?.value ?? '';
    const marker = data?.marker ?? '{{';
    const suffix = marker === '{{' ? '}}' : '';
    const content = `${marker}${String(value)}${suffix}`;
    const target = element?.nodeType ? element : element?.[0];
    if (target) {
      target.textContent = content;
      return target;
    }
    return content;
  }

  private renderMentionItemTemplate(itemData, _itemIndexOrElement, _maybeElement) {
    const item = itemData?.itemData ?? itemData;
    const label = item?.description ? String(item.description) : '';
    const value = item?.value ? String(item.value) : '';
    const collectionDescription = item?.collectionDescription ? String(item.collectionDescription) : '';
    const isGroupStart = !!(item?.isFirstInGroup && collectionDescription);
    const text = label || value;
    const collectionName = item?.collection ? EmailTemplateContextMap.get(item.collection) : '';

    const element = document.createElement('div');
    element.className = 'html-editor__mention-item';
    element.setAttribute('data-collection-description', collectionDescription);
    element.classList.toggle('html-editor__mention-item--first-in-group', isGroupStart);

    if (isGroupStart) {
      const groupTitleElement = document.createElement('div');
      groupTitleElement.className = 'html-editor__mention-item-group-title';
      groupTitleElement.textContent = collectionName;
      element.appendChild(groupTitleElement);
    }

    const textElement = document.createElement('span');
    textElement.className = 'html-editor__mention-item-text';
    textElement.textContent = text;
    element.appendChild(textElement);
    return element;
  }
}
